/**
 * THE hires Table's `Export CSV` (2026-09-27) — built
 * CLIENT-SIDE from the hire lines already loaded, because there is NO hires
 * export endpoint (`GET /api/orders/export` exports ORDERS) and the rows the
 * user is looking at (the status TAB + the search box, over every page) exist
 * only in the page's memory — the same reason `utils/gagCsvExport.js` is
 * client-side.
 *
 * 🔑 THE FILE FORMAT IS THE ORDERS EXPORT'S, byte for byte in SHAPE
 * (`server/routes/orders.js`, the `/export` handler): a UTF-8 BOM, every
 * present field double-quoted through the SAME `csvEscape` (formula-injection
 * guard included — `rowsToCsv` from `gagCsvExport.js`, never a third copy),
 * `\r\n` line endings and a trailing newline, money as `toFixed(2)` text.
 * 🪤 **There is no separate "Excel" file** (owner 2026-10-28): that option was
 * this same CSV text saved as `.xls` / `application/vnd.ms-excel`, which Excel
 * warns about on open. It is the one `.csv` now (`CSV_EXT` / `CSV_MIME`), exactly
 * what the server's export sends.
 *
 * One row per hire LINE — the Table's own unit — with the Table's columns plus
 * the rate fields and dates as stored, so the sheet can be re-sorted/summed.
 */
import { rowsToCsv, downloadTextFile, CSV_EXT, CSV_MIME } from './gagCsvExport';
import { toLocalISO } from './dateFormat';
import { roundMoney } from './money';
import { numeric } from './listPdf';
import { getOrderNumber } from './orderTitle';
import { HIRE_STATUS_BADGES } from '../constants/colors';

// 🔀 REORDERED 2026-09-28 to follow the Table's new column order (owner:
// "Dept Hires table needs the columns arranged a little more like Dept
// orders"): PO/Order, Requested By, Supplier, Item, Status, then the fields
// the Table does not show. `PO Number` is a new column — `h.po_number` is on
// the row (2026-09-28, `c13a99da`) but was not exported before.
export const HIRE_EXPORT_HEADERS = Object.freeze([
  'PO Number', 'Order Number', 'Requested By', 'Supplier', 'Item', 'Status',
  'Hire From', 'Hire To', 'Returned', 'Days', 'Quantity',
  'Rate Type', 'Daily Rate', 'Weekly Rate', 'Total Hire Rate',
  'Expected Cost', 'Cost To Date', 'Order Status',
]);

/**
 * Money as the orders export writes it — 2dp text — over root `CLAUDE.md`'s
 * THREE states (`utils/moneyOrAbsent.js`): ABSENT (null / undefined / blank or
 * whitespace-only) → an empty cell; MALFORMED (present but not a finite amount,
 * or a finite amount whose rounding overflows) → the RAW TEXT it holds, LOUD,
 * never the empty cell absence gets (review round 3, F5: they shared a marker,
 * and `roundMoney` answering `null` threw inside `.toFixed` and aborted the
 * whole export). 🪤 The server's order export writes `Number(x).toFixed(2)`,
 * i.e. the literal `NaN`, for the same input; the raw text is chosen here
 * because it says WHAT was stored, which `NaN` does not.
 */
function money(value) {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  const n = numeric(value);
  const r = n == null ? null : roundMoney(n);
  if (r != null) return r.toFixed(2);
  const raw = String(value);
  return raw.trim() === '' ? JSON.stringify(value) : raw; // `[]` must not read as absent
}

function hireStatusLabel(status) {
  return (HIRE_STATUS_BADGES[status] && HIRE_STATUS_BADGES[status].label) || status;
}

function hireRow(h) {
  return [
    h.po_number || null,
    getOrderNumber(h) || null,
    h.requested_by,
    h.supplier_name,
    h.description,
    hireStatusLabel(h.hire_status),
    h.hire_from,
    h.hire_to,
    h.actual_return_date,
    h.hire_days,
    h.quantity,
    h.hire_rate_type,
    money(h.daily_rate),
    money(h.weekly_rate),
    money(h.total_hire_rate),
    money(h.expected_cost),
    money(h.current_cost),
    h.order_status,
  ];
}

/** The whole CSV text for these hire lines. An empty list is the header row alone. */
export function hiresToCsv(hires) {
  return rowsToCsv(HIRE_EXPORT_HEADERS, (hires || []).map(hireRow));
}

/** `hires-export-<YYYY-MM-DD>.csv` — the orders export's filename shape. */
export function hireExportFilename(now = new Date()) {
  return `hires-export-${toLocalISO(now)}${CSV_EXT}`;
}

/** Build and save the export — always CSV. */
export function downloadHireExport(hires) {
  downloadTextFile(hiresToCsv(hires), hireExportFilename(), CSV_MIME);
}
